/**
 * Widget Wrapper - Einheitliches Layout und Verhalten für alle Widgets
 */

import { ReactNode, createContext, useContext } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { WidgetProps } from '@/types/widget'

const WidgetPreviewContext = createContext(false)

export function WidgetPreviewProvider({
  children,
  value = true
}: {
  children: ReactNode
  value?: boolean
}) {
  return (
    <WidgetPreviewContext.Provider value={value}>
      {children}
    </WidgetPreviewContext.Provider>
  )
}

interface WidgetWrapperProps extends Omit<WidgetProps, 'onOpenApp'> {
  children: ReactNode
  className?: string
  title?: string
  icon?: ReactNode
  onClick?: () => void
  onOpenApp?: WidgetProps['onOpenApp']
  allowHover?: boolean
  staticPreview?: boolean
}

export function WidgetWrapper({
  config: _config,
  children,
  className,
  title,
  icon,
  onClick,
  editMode = false,
  allowHover = true,
  staticPreview = false
}: WidgetWrapperProps) {
  const previewContext = useContext(WidgetPreviewContext)
  const isStaticPreview = staticPreview || previewContext
  const handleClick = () => {
    // Wenn im Edit-Modus, kein Click-Handling
    if (editMode) return

    // Wenn ein onClick-Handler übergeben wurde, nutze diesen
    if (onClick) {
      onClick()
      return
    }

    // Default: Kein Action (Widgets können intern ihre eigenen Klicks handhaben)
  }

  const renderContent = () => {
    if (!title) {
      return <div className="h-full w-full">{children}</div>
    }

    return (
      <div
        className={cn(
          'rounded-2xl shadow-xl border p-4 h-full w-full flex flex-col overflow-hidden transition-all',
          allowHover && 'hover:border-slate-300 dark:hover:border-slate-600'
        )}
        style={{
          background: 'var(--window-background)',
          borderColor: 'var(--window-border-color)',
        }}
      >
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="flex items-center justify-center text-sm">
                {icon}
              </span>
            )}
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
              {title}
            </h3>
          </div>
        </div>
        <div className="flex-1 w-full overflow-y-auto min-h-0">
          {children}
        </div>
      </div>
    )
  }

  if (isStaticPreview) {
    return (
      <div
        className={cn(
          'relative h-full w-full',
          editMode && 'cursor-move',
          className
        )}
        onClick={handleClick}
      >
        {renderContent()}

        {editMode && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: 'var(--cdx-color-accent)', opacity: 0.05 }}
          />
        )}
      </div>
    )
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'relative h-full w-full',
        editMode && 'cursor-move',
        className
      )}
      onClick={handleClick}
    >
      {/* Widget Content */}
      {renderContent()}

      {/* Edit Mode Overlay */}
      {editMode && (
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: 'var(--cdx-color-accent)', opacity: 0.05 }} />
      )}
    </motion.div>
  )
}

/**
 * Simplified Widget Card for clickable widgets that open apps
 */
interface ClickableWidgetWrapperProps extends WidgetWrapperProps {
  appId?: string
  deepLinkParams?: Record<string, string | number | boolean | undefined>
}

export function ClickableWidgetWrapper({
  appId,
  deepLinkParams,
  onOpenApp,
  children,
  ...props
}: ClickableWidgetWrapperProps) {
  const handleWidgetClick = () => {
    if (appId && onOpenApp) {
      onOpenApp(appId as any, deepLinkParams)
    }
  }

  return (
    <WidgetWrapper {...props} onOpenApp={onOpenApp} onClick={handleWidgetClick}>
      {children}
    </WidgetWrapper>
  )
}
